import React, { useState, useEffect } from 'react';
import { Table, Typography, Tag, Input, DatePicker, Space, Button } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from '../../utils/request';
import dayjs from 'dayjs';
import type { RangePickerProps } from 'antd/es/date-picker';

const { Title } = Typography;
const { RangePicker } = DatePicker;

interface RedemptionRecord {
  _id: string;
  code: string;
  name: string;
  usedAt: string;
  usedBy: { username: string };
  ekokNumber: string;
  customerName: string;
}

const RedemptionRecords: React.FC = () => {
  const [records, setRecords] = useState<RedemptionRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    dateRange: null as any
  });

  const fetchRecords = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/coupons/records?page=${page}&limit=${pageSize}`);
      setRecords(response.data.records);
      setPagination({
        current: page,
        pageSize,
        total: response.data.pagination.total
      });
    } catch (error: any) {
      console.error('获取核销记录失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleSearch = () => {
    fetchRecords(1, pagination.pageSize);
  };

  const handleReset = () => {
    setFilters({ search: '', dateRange: null });
    fetchRecords(1, pagination.pageSize);
  };

  const columns = [
    {
      title: '优惠码',
      dataIndex: 'code',
      key: 'code',
      width: 120,
    },
    {
      title: '优惠券名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'EKOK号码',
      dataIndex: 'ekokNumber',
      key: 'ekokNumber',
      width: 120,
    },
    {
      title: '客人姓名',
      dataIndex: 'customerName',
      key: 'customerName',
      width: 100,
    },
    {
      title: '核销员',
      dataIndex: 'usedBy',
      key: 'usedBy',
      width: 100,
      render: (user: any) => user?.username,
    },
    {
      title: '核销时间',
      dataIndex: 'usedAt',
      key: 'usedAt',
      width: 150,
      render: (date: string) => (
        <div>
          <div>{dayjs(date).format('YYYY-MM-DD')}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            {dayjs(date).format('HH:mm:ss')}
          </div>
        </div>
      ),
    },
    {
      title: '状态',
      key: 'status',
      width: 80,
      render: () => <Tag color="green">已核销</Tag>,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={3}>核销记录</Title>
      
      <div style={{ marginBottom: '16px', background: '#fafafa', padding: '16px', borderRadius: '6px' }}>
        <Space wrap>
          <Input
            placeholder="搜索优惠码、EKOK号码或客人姓名"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            style={{ width: 250 }}
            prefix={<SearchOutlined />}
          />
          <RangePicker
            placeholder={['开始日期', '结束日期']}
            value={filters.dateRange}
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
          />
          <Button type="primary" onClick={handleSearch}>
            搜索
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            重置
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={records}
        rowKey="_id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
          onChange: (page, pageSize) => {
            fetchRecords(page, pageSize);
          }
        }}
        scroll={{ x: 800 }}
      />
    </div>
  );
};

export default RedemptionRecords;